import { dateConfigMap } from 'nepali-date-converter';
import { engineAdToBs, engineBsToAd, engineFormatBs, engineTodayBs, type EngineDate } from './bsEngine';

export class DateConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DateConversionError';
  }
}

/** A calendar date. `month` is 1-12 (Baisakh=1..Chaitra=12 for BS; Jan=1..Dec=12 for AD). */
export interface BsDate {
  year: number;
  month: number;
  day: number;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIso(iso: string): { year: number; month: number; day: number } {
  const match = ISO_DATE_RE.exec(iso);
  if (!match) {
    throw new DateConversionError(`Expected an ISO date string "YYYY-MM-DD", got: ${iso}`);
  }
  const [, y, m, d] = match as unknown as [string, string, string, string];
  return { year: Number(y), month: Number(m), day: Number(d) };
}

function formatIso({ year, month, day }: EngineDate): string {
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function assertValidAdCalendarDate(d: { year: number; month: number; day: number }): void {
  // JS Date silently normalizes overflow (e.g. Feb 30 -> Mar 2) — round-trip
  // through it to catch dates that aren't actually valid on the AD calendar.
  const check = new Date(d.year, d.month - 1, d.day);
  if (check.getFullYear() !== d.year || check.getMonth() !== d.month - 1 || check.getDate() !== d.day) {
    throw new DateConversionError(`Not a valid AD calendar date: ${formatIso(d)}`);
  }
}

const BS_MONTH_KEYS = [
  'Baisakh', 'Jestha', 'Asar', 'Shrawan', 'Bhadra', 'Aswin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
] as const;

/** Number of days in a given BS month, from the library's published calendar data. */
function bsMonthLength(year: number, month1to12: number): number {
  const yearConfig = dateConfigMap[String(year)];
  if (!yearConfig) {
    throw new DateConversionError(
      `Bikram Sambat year ${year} is outside the supported calendar range (roughly 2000-2090 BS).`,
    );
  }
  const key = BS_MONTH_KEYS[month1to12 - 1];
  if (!key) throw new DateConversionError(`Invalid BS month: ${month1to12}`);
  return yearConfig[key];
}

function assertValidBsCalendarDate(d: BsDate): void {
  const length = bsMonthLength(d.year, d.month);
  if (d.day < 1 || d.day > length) {
    throw new DateConversionError(
      `${d.day} is not a valid day in BS month ${d.month}/${d.year} (which has ${length} days).`,
    );
  }
}

/** Converts an AD date (ISO "YYYY-MM-DD") to its Bikram Sambat equivalent. */
export function adToBs(iso: string): BsDate {
  const ad = parseIso(iso);
  assertValidAdCalendarDate(ad);
  try {
    return engineAdToBs(ad);
  } catch (err) {
    throw new DateConversionError(`Could not convert AD date ${iso} to BS: ${(err as Error).message}`);
  }
}

/** Converts a Bikram Sambat date to its AD equivalent, as an ISO "YYYY-MM-DD" string. */
export function bsToAd(bs: BsDate): string {
  assertValidBsCalendarDate(bs);
  try {
    return formatIso(engineBsToAd(bs));
  } catch (err) {
    throw new DateConversionError(`Could not convert BS date ${formatIso(bs)} to AD: ${(err as Error).message}`);
  }
}

/** Formats a BS date for display, e.g. "Shrawan 17, 2082" (en) or the Devanagari equivalent (ne). */
export function formatBs(bs: BsDate, locale: 'en' | 'ne' = 'en'): string {
  assertValidBsCalendarDate(bs);
  return engineFormatBs(bs, locale);
}

/** Today's date in the Bikram Sambat calendar. */
export function todayBs(): BsDate {
  return engineTodayBs();
}

/** Today's date in the AD (Gregorian) calendar, as an ISO "YYYY-MM-DD" string. */
export function todayAd(): string {
  const now = new Date();
  return formatIso({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() });
}

export interface BillDateSync {
  billDateAd: string | null;
  billDateBs: string | null;
}

/**
 * Keeps the AD and BS bill-date fields in sync whenever the user edits
 * EITHER one — the IRD form only ever takes the AD value (BS is shown purely
 * as a convenience for the user's own calendar), so if only the edited
 * field were saved, the other would go stale and business validation (which
 * checks `billDateAd`) would silently validate against outdated data.
 *
 * - An edit that isn't yet a complete "YYYY-MM-DD" string (still being
 *   typed) returns `null` — the caller should update only the edited
 *   field's raw text and leave the other one alone, rather than blanking it
 *   out on every keystroke.
 * - A complete but empty edit (field cleared) resolves both sides to null.
 * - A complete but invalid calendar date (e.g. day 35) resolves the OTHER
 *   side to null rather than leaving it silently mismatched.
 * - A complete, valid date resolves both sides via real calendar conversion.
 */
export function syncBillDateEdit(edited: 'ad' | 'bs', newValue: string): BillDateSync | null {
  const value = newValue.trim();
  if (value.length === 0) return { billDateAd: null, billDateBs: null };
  if (!ISO_DATE_RE.test(value)) return null;

  const [, y, m, d] = ISO_DATE_RE.exec(value) as unknown as [string, string, string, string];
  const parts = { year: Number(y), month: Number(m), day: Number(d) };

  try {
    if (edited === 'bs') {
      return { billDateBs: value, billDateAd: bsToAd(parts) };
    }
    return { billDateAd: value, billDateBs: formatIso(adToBs(value)) };
  } catch (err) {
    if (err instanceof DateConversionError) {
      return edited === 'bs' ? { billDateBs: value, billDateAd: null } : { billDateAd: value, billDateBs: null };
    }
    throw err;
  }
}

/**
 * Nepal's government fiscal year always runs Shrawan 1 (BS month 4) through
 * the last day of the following Asar (BS month 3) — a fixed calendar rule,
 * not a value that needs yearly reconfiguration. Given a reference AD date
 * (defaults to today), returns that fiscal year's AD start/end boundaries —
 * used to validate that a bill date falls within IRD's "active fiscal year"
 * window, matching a rule verified on the live prize.ird.gov.np form.
 */
export function nepaliFiscalYearWindow(refAd?: string): { startAd: string; endAd: string } {
  const refBs = refAd ? adToBs(refAd) : todayBs();
  const fyStartBsYear = refBs.month >= 4 ? refBs.year : refBs.year - 1;
  const fyEndBsYear = fyStartBsYear + 1;
  const asarLength = bsMonthLength(fyEndBsYear, 3);
  return {
    startAd: bsToAd({ year: fyStartBsYear, month: 4, day: 1 }),
    endAd: bsToAd({ year: fyEndBsYear, month: 3, day: asarLength }),
  };
}
